'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import Badge from '@/components/ui/Badge';
import AppImage from '@/components/ui/AppImage';
import type { Job } from './ClientJobPortalContent';

interface CompletionReportViewerProps {
  job: Job;
}

export default function CompletionReportViewer({ job }: CompletionReportViewerProps) {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const photos: string[] = (job as any).completionPhotos || [];

  return (
    <div className="space-y-5">
      {/* Report header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Service</p>
          <p className="text-sm font-semibold text-foreground mt-0.5">{job.serviceType} — {job.serviceSubType}</p>
        </div>
        <Badge variant="completed">Completed</Badge>
      </div>

      {/* Report details grid */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: 'Completion Date', value: job.completionDate ?? '—', icon: 'CalendarDaysIcon' },
          { label: 'Assigned Team', value: job.assignedTeam ?? '—', icon: 'UserGroupIcon' },
          { label: 'Payment Status', value: job.paymentStatus === 'paid' ? 'Paid in Full' : 'Invoice Sent', icon: 'CreditCardIcon' },
          { label: 'Invoice Total', value: `$${job.quoteTotal?.toFixed(2)} inc. GST`, icon: 'DocumentTextIcon' },
        ].map((item) => (
          <div key={`cr-${item.label.replace(/\s/g, '-')}`} className="bg-muted/30 rounded-lg p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Icon name={item.icon as any} size={13} variant="outline" className="text-muted-foreground" />
              <p className="text-xs text-muted-foreground">{item.label}</p>
            </div>
            <p className="text-sm font-semibold text-foreground">{item.value}</p>
          </div>
        ))}
      </div>

      {/* Team notes */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Team Notes</p>
        <div className="bg-muted/30 rounded-lg p-3">
          <p className="text-xs text-foreground leading-relaxed">
            {(job as any).completionNotes || 'Job completed as per quote. Property left clean and tidy.'}
          </p>
        </div>
      </div>

      {/* Photo gallery */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          Completion Photos {photos.length > 0 ? `(${photos.length})` : ''}
        </p>
        {photos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 bg-muted/20 rounded-xl border border-dashed border-border">
            <Icon name="PhotoIcon" size={28} variant="outline" className="text-muted-foreground mb-2" />
            <p className="text-xs text-muted-foreground">No completion photos uploaded yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {photos.map((url, idx) => (
              <button
                key={url}
                type="button"
                onClick={() => setLightboxUrl(url)}
                className="group relative overflow-hidden rounded-lg aspect-square"
              >
                <AppImage
                  src={url}
                  alt={`Completion photo ${idx + 1}`}
                  width={200}
                  height={200}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/20 transition-all duration-150 rounded-lg flex items-center justify-center">
                  <Icon name="MagnifyingGlassPlusIcon" size={20} variant="outline" className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Payment section */}
      {job.paymentStatus === 'invoiced' && (
        <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 flex items-start gap-3">
          <Icon name="CreditCardIcon" size={18} variant="outline" className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800">Invoice awaiting payment</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Your invoice for ${job.quoteTotal?.toFixed(2)} has been sent to your email. Please pay within 7 days.
            </p>
          </div>
          <button
            type="button"
            className="flex-shrink-0 px-3 py-1.5 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 transition-colors"
          >
            Pay Now
          </button>
        </div>
      )}

      {job.paymentStatus === 'paid' && (
        <div className="border border-green-200 bg-green-50 rounded-xl p-3 flex items-center gap-2">
          <Icon name="CheckCircleIcon" size={16} variant="outline" className="text-green-600" />
          <p className="text-xs text-green-700 font-medium">Payment received — thank you!</p>
        </div>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[200] bg-foreground/80 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setLightboxUrl(null)}
        >
          <div className="relative max-w-2xl w-full animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setLightboxUrl(null)}
              className="absolute -top-10 right-0 text-white hover:text-muted-foreground transition-colors"
              aria-label="Close photo"
            >
              <Icon name="XMarkIcon" size={24} variant="outline" />
            </button>
            <AppImage
              src={lightboxUrl}
              alt="Completion photo full view"
              width={800}
              height={600}
              className="w-full rounded-xl shadow-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
}
